import React from 'react';

import { Box, Divider, Button, ButtonBase, styled } from '@mui/material';
import { Delete, DragIndicator } from '@mui/icons-material';
import { Column, Row } from 'components/Layout';
import TextField from 'components/FormElements/AppTextInput';
import {
  getMaxSizeForUploadType,
  getValidFileTypesForUploadType,
} from 'further-ui/config';
import { dataRoomStatusOptions } from 'further-ui/labels';
import { DataRoomFormData } from '../formData';
import { Control, Controller, useFieldArray } from 'react-hook-form';
import DraggableList from 'components/DraggableList';
import useFileUpload from 'hooks/ui/useFileUpload';
import { UploadType } from 'further-types/files';
import FormField from 'further-ui/components/FormField';
import BasicSelect from 'further-ui/components/BasicSelect';
import FileDrop from 'further-ui/components/FileDrop';

const StyledDraggableList = styled(DraggableList)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

type Props = {
  control: Control<DataRoomFormData>;
};

const DataRoomGeneral: React.FC<Props> = ({ control }) => {
  const { loading: isUploading, uploadFile } = useFileUpload();

  const {
    fields: keyInformation,
    remove: removeKeyInformationRow,
    append: appendKeyInformationRow,
    move,
  } = useFieldArray({
    control,
    name: 'keyInformation',
  });

  return (
    <Column spacing="lg">
      <FormField label="Data room status" size="md">
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <BasicSelect {...field} fullWidth options={dataRoomStatusOptions} />
          )}
        />
      </FormField>
      <Divider flexItem />
      <FormField label="Data room headline" size="md">
        <Controller
          name="headline"
          control={control}
          render={({ field }) => <TextField {...field} size="small" />}
        />
      </FormField>
      <FormField label="Data room subheading" size="md">
        <Controller
          name="subheading"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              multiline
              rows={5}
              size="small"
              placeholder="Data room subheading"
            />
          )}
        />
      </FormField>
      <Divider flexItem />
      <FormField
        label="Contact"
        extraInformation="Add the main point of contact for this tranche. Their details will be shown in your data room."
        size="md"
      >
        <Column spacing="sm">
          <Controller
            name="contact.image"
            control={control}
            render={({ field: { value, onChange } }) => {
              return (
                <FileDrop
                  inputTitle="Image"
                  files={value ? [{ ...value, isUploading }] : []}
                  onDeleteFile={() => {
                    onChange(null);
                  }}
                  onDropFile={(files) => {
                    const imageFile = files[0];
                    if (!imageFile) return;

                    onChange({
                      uri: imageFile.previewUri,
                      objectKey: '',
                      fileName: imageFile.fileName,
                    });

                    const handleFileUpload = async (file: File) => {
                      const { data } = await uploadFile(
                        file,
                        UploadType.DataRoomDocuments,
                      );
                      if (!data) return;
                      onChange({
                        uri: data?.url,
                        objectKey: data.filePath,
                        fileName: imageFile.fileName,
                      });
                    };
                    handleFileUpload(imageFile.fileData);
                  }}
                  sizeRestriction={{
                    unit: 'mb',
                    value: getMaxSizeForUploadType(
                      UploadType.DataRoomDocuments,
                      'mb',
                    ),
                  }}
                  supportedFormats={getValidFileTypesForUploadType(
                    UploadType.DataRoomDocuments,
                  )}
                />
              );
            }}
          />
          <Controller
            name="contact.name"
            control={control}
            render={({ field }) => (
              <TextField {...field} size="small" placeholder="Name" />
            )}
          />
          <Controller
            name="contact.email"
            control={control}
            render={({ field }) => (
              <TextField {...field} size="small" placeholder="Email address" />
            )}
          />
          <Controller
            name="contact.phone"
            control={control}
            render={({ field }) => (
              <TextField {...field} size="small" placeholder="Phone number" />
            )}
          />
        </Column>
      </FormField>
      <FormField
        label="Key information"
        extraInformation="Add details like fund size, minimum commitment or strategy. This information will show in your data room page."
      >
        <Column spacing="sm">
          <StyledDraggableList
            onReorder={({ oldIndex, newIndex }) => {
              move(oldIndex, newIndex);
            }}
          >
            {keyInformation.map((field, index) => (
              <DraggableList.Item
                withDragHandle
                key={field.id}
                id={index.toString()}
              >
                <Row spacing="sm">
                  <Controller
                    key={field.id}
                    name={`keyInformation.${index}.fieldName`}
                    control={control}
                    render={({ field: controllerField }) => (
                      <TextField
                        {...controllerField}
                        multiline
                        rows={3}
                        size="small"
                        placeholder="Field name"
                      />
                    )}
                  />
                  <Controller
                    key={field.id}
                    name={`keyInformation.${index}.value`}
                    control={control}
                    render={({ field: controllerField }) => (
                      <TextField
                        {...controllerField}
                        multiline
                        rows={3}
                        size="small"
                        placeholder="Field name"
                      />
                    )}
                  />
                  <Row centered justify="center">
                    <ButtonBase>
                      <Delete
                        onClick={() => {
                          removeKeyInformationRow(index);
                        }}
                      />
                    </ButtonBase>
                    <DraggableList.Handle>
                      <ButtonBase>
                        <DragIndicator />
                      </ButtonBase>
                    </DraggableList.Handle>
                  </Row>
                </Row>
              </DraggableList.Item>
            ))}
          </StyledDraggableList>
          <Box>
            <Button
              onClick={() =>
                appendKeyInformationRow({ fieldName: '', value: '' })
              }
              size="large"
              variant="outlined"
            >
              Add new
            </Button>
          </Box>
        </Column>
      </FormField>
    </Column>
  );
};

export default DataRoomGeneral;
